import { type ChangeEvent, type InputHTMLAttributes, type PropsWithChildren, useId } from 'react'

import { State } from '#/global/component/Form/State'
import type { DefaultProps } from '#/global/component/Form/types'

import { Input as Helper } from '#/global/helper/DekForms/Input'

export interface Props<Value extends number | string = string> extends DefaultProps, Omit<InputProps, 'onChange' | 'size' | 'type'> {
    defaultValue?: Value
    hideSpinButton?: boolean
    onChange?: (value: Value, event: ChangeEvent<HTMLInputElement>) => void
    /**
     * **Must be used with `defaultValue` instead of `value`**
     */
    onChangeLazy?: (value: Value, event: ChangeEvent<HTMLInputElement>) => void
    value?: Value
}

type InputProps = InputHTMLAttributes<HTMLInputElement>

interface NumberProps extends Props<number> {
    type: 'number'
}

interface TextProps extends Props {
    type?: 'color' | 'date' | 'datetime-local' | 'email' | 'hidden' | 'password' | 'search' | 'tel' | 'text' | 'time' | 'url'
}

export const Input = ({
    children,
    className,
    hideSpinButton,
    onBlur,
    onChange,
    onChangeLazy,
    size,
    state,
    type,
    useSizeOnState,
    ...props
}: PropsWithChildren<TextProps | NumberProps>) => {
    const id = useId()

    const { disabled, required } = props

    const handleChange: InputProps['onChange'] = (event) => {
        if (!onChange) {
            return
        }
        const { value } = event.target
        if (type === 'number') {
            onChange(Number(value) || Number.NaN, event)
            return
        }
        onChange(value, event)
    }

    const handleBlur: InputProps['onBlur'] = (event) => {
        if (onChangeLazy) {
            const { value } = event.target
            if (type === 'number') {
                onChangeLazy(Number(value) || Number.NaN, event)
                return
            }
            onChangeLazy(value, event)
        }
        onBlur?.(event)
    }

    const formWidth = Helper.getFormSize(size)

    const classNameList = []
    formWidth && classNameList.push(formWidth)
    state && classNameList.push(state.level)
    // push custom class
    className && classNameList.push(className)
    hideSpinButton && classNameList.push('hide-spin-button')

    return (
        <>
            {children !== undefined && (
                <label htmlFor={id}>
                    {children}
                    {required && <span className="dek-form-required-indicator">*</span>}
                </label>
            )}
            <input
                className={classNameList.join(' ')}
                id={id}
                onBlur={handleBlur}
                onChange={handleChange}
                role="textbox"
                type={type}
                {...props}
            />
            {state && !disabled && state.message && <State formWidth={useSizeOnState ? formWidth : undefined} state={state} />}
        </>
    )
}
